import React, { useState, useEffect } from "react";
import { getStorage, ref, uploadBytesResumable } from "firebase/storage";
import { getFirestore, doc, updateDoc, onSnapshot } from "firebase/firestore";

const UploadFile = ({ selectedPatient, onClose }) => {
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [files, setFiles] = useState({});

  const storage = getStorage();
  const db = getFirestore();

  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const ALLOWED_TYPES = [
    "application/pdf",
    "image/png",
    "image/jpg",
    "image/jpeg",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  useEffect(() => {
    if (!selectedPatient) return;

    const patientRef = doc(db, "registeredPatients", selectedPatient.id);

    const unsubscribe = onSnapshot(
      patientRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setFiles(data.files || {});
        }
      },
      () => {
        setError("Ocurrió un error al cargar los archivos.");
      },
    );

    return () => unsubscribe();
  }, [selectedPatient, db]);

  const isValidFilename = (filename) => {
    const forbiddenPatterns = [
      /\.\.\//,
      /\.\.\\/,
      /[<>:"|?*]/,
      /<script>/i,
      /\0/,
    ];

    return (
      filename.length <= 255 &&
      !forbiddenPatterns.some((pattern) => pattern.test(filename))
    );
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!isValidFilename(selectedFile.name)) {
      setError(
        "El nombre del archivo no es válido o podría representar un riesgo.",
      );
      setFile(null);
      return;
    }

    if (!ALLOWED_TYPES.includes(selectedFile.type)) {
      setError(
        "El tipo de archivo no es válido. Se permiten archivos PDF, PNG, JPG, DOC y DOCX.",
      );
      setFile(null);
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError("El archivo supera el tamaño permitido (máximo 5 MB).");
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError("");
    setSuccess("");
  };

  const handleUpload = async () => {
    if (!file || !selectedPatient) {
      setError("Debes elegir un archivo válido.");
      return;
    }

    try {
      const timestamp = Date.now();
      const uniqueFileName = `${timestamp}_${file.name}`;

      const storageRef = ref(
        storage,
        `patientFiles/${selectedPatient.id}/${uniqueFileName}`,
      );

      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        () => {
          setError("Ocurrió un error durante la subida del archivo.");
        },
        async () => {
          const patientRef = doc(db, "registeredPatients", selectedPatient.id);

          const updatedFiles = {
            ...files,
            [uniqueFileName]: {
              path: `patientFiles/${selectedPatient.id}/${uniqueFileName}`,
              originalName: file.name,
              uploadedAt: new Date().toISOString(),
            },
          };

          await updateDoc(patientRef, { files: updatedFiles });

          setSuccess("El archivo se subió correctamente.");
          setUploadProgress(0);
          setFile(null);
        },
      );
    } catch (error) {
      setError("Ocurrió un error al subir el archivo.");
    }
  };

  return (
    <div>
      <h3>Cargar archivo</h3>
      {error && <p style={{ color: "red" }}>{error}</p>}
      {success && <p style={{ color: "green" }}>{success}</p>}
      {uploadProgress > 0 && (
        <p>Subida en progreso: {Math.round(uploadProgress)}%</p>
      )}
      {!success ? (
        <>
          <input type="file" onChange={handleFileChange} />
          <button onClick={handleUpload} className="smallButton">
            Subir
          </button>
        </>
      ) : (
        <button className="smallButton" onClick={onClose}>
          Cerrar
        </button>
      )}
    </div>
  );
};

export default UploadFile;
